/**
 * F3 — CLP plan shell (v2).
 * Header CRUD (/construction/linked/plan) is UNCHANGED per contract §D.
 * Child <ProjectStatus> renders the reshaped clp_line rows (wbsStageId/floorTarget/clpLineId).
 * No functional change beyond passing the new shape through.
 */
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../services/api/axios";
import { DeleteButton, EditButton, IconButton } from "../../components/Buttons";
import CustomCollapseCard from "../CustomCollapseCard";
import { toast } from "react-toastify";
import { FaPlus } from "react-icons/fa";
import usePrivilege from "../../hooks/usePrivilege";
import ProjectStatus from "./ProjectStatus";
import ConfirmationModal from "../../components/ConfirmationModal";
import { FormModal, Form, Field, FormSubmit } from "../form";
import { constructionPlanSchema } from "../../services/projects/schemas";

const ConstructionPlan = ({ projectId, phaseId }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedPlanId, setExpandedPlanId] = useState(null);
    const { hasPrivilege } = usePrivilege();
    const [openNewPlanModal, setOpenNewPlanModal] = useState(false);
    const [projectStatus, setProjectStatus] = useState([]);
    const [editingPlan, setEditingPlan] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [planToDeleteId, setPlanToDeleteId] = useState(null);


    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get(
                    `/construction/linked/plan`,
                    { params: { projectPhaseId: phaseId } }
                );
                setPlans(response);
                // Open the first plan by default if available
                if (response && response.length > 0) {
                    setExpandedPlanId(response[0].constructionLinkedPlanId);
                }
            } catch (err) {
                console.error("Error fetching construction plans:", err);
                setError("Failed to fetch construction plans.");
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, [projectId]);

    const toggleCard = (planId) => {
        setExpandedPlanId((prevId) => (prevId === planId ? null : planId));
    };

    const handleCreatePlan = async ({ name }) => {
        try {
            const response = await axiosInstance.post(
                `/construction/linked/plan`,
                [{ projectPhaseId: phaseId, name }]
            );
            if (response.length > 0) {
                setPlans((p) => [...p, response[0]]);
                toast.success("Plan created successfully!");
            } else {
                toast.error("Failed to create construction plan. Please try again.");
            }
            setOpenNewPlanModal(false);
        } catch (err) {
            console.error("Error creating construction plan:", err);
            setError("Failed to create construction plan.");
            toast.error(err?.detail || "Failed to create construction plan.");
        }
    };

    const handleEditPlan = (plan) => {
        setEditingPlan(plan);
        setOpenNewPlanModal(true);
    };

    const handleUpdatePlan = async ({ name }) => {
        try {
            await axiosInstance.put(
                `/construction/linked/plan`,
                { name, projectPhaseId: editingPlan.projectPhase?.projectPhaseId || phaseId },
                { params: { constructionLinkedPlanId: editingPlan.constructionLinkedPlanId } },
            );
            setPlans((plans) =>
                plans.map((plan) =>
                    plan.constructionLinkedPlanId === editingPlan.constructionLinkedPlanId
                        ? { ...plan, name }
                        : plan,
                ),
            );
            toast.success("Plan updated successfully!");
            setEditingPlan(null);
            setOpenNewPlanModal(false);
        } catch (err) {
            console.error("Error updating construction plan:", err);
            setError("Failed to update construction plan.");
            toast.error(err?.detail || "Failed to update construction plan.");
        }
    };

    const handleDeletePlan = async () => {
        setIsDeleting(true);  // Set deleting state to true
        try {
            await axiosInstance.delete(
                `/construction/linked/plan`,
                {
                    params: { construction_linked_plan_id: planToDeleteId }  // Correct parameter name
                }
            );

            setPlans(plans.filter((plan) => plan.constructionLinkedPlanId !== planToDeleteId)); // Remove deleted plan from state
            toast.success("Plan deleted successfully!");
        } catch (err) {
            console.error("Error deleting construction plan:", err);
            setError("Failed to delete construction plan.");
            toast.error("Failed to delete construction plan.");
        } finally {
            setIsDeleting(false); // Reset deleting state
            setShowConfirmationModal(false); // Close the modal after deletion
            setPlanToDeleteId(null); // Reset the planToDeleteId
        }
    };


    const handleOpenConfirmationModal = (planId) => {
        setPlanToDeleteId(planId);
        setShowConfirmationModal(true);
    };

    const handleCloseConfirmationModal = () => {
        setShowConfirmationModal(false);
        setPlanToDeleteId(null);
    };


    const renderPlanContent = (plan) => {
        return (
            <ProjectStatus
                projectId={projectId}
                setProjectStatusType={setProjectStatus}
                constructionLinkedPlanId={plan.constructionLinkedPlanId}
            />
        );
    };

    const handleCancelEdit = () => {
        setEditingPlan(null);
        setOpenNewPlanModal(false);
    };

    const renderAdditionalButtons = (plan) => {
        return (
            <div className="flex justify-end space-x-2">
                {hasPrivilege("canEdit") && (
                    <>
                     <EditButton  onClick={(e) => {
                                e.stopPropagation();
                                handleEditPlan(plan);
                            }} />
                        {/* <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditPlan(plan);
                            }}
                            color="black"
                            className="p-2"
                        >
                            <Edit size={17.5} />
                        </Button> */}
                        <DeleteButton  onClick={(e) => {
                                e.stopPropagation();
                                handleOpenConfirmationModal(plan.constructionLinkedPlanId);
                            }}
                            disabled={isDeleting} />
                        
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="p-2 ">
            <ConfirmationModal
                isOpen={showConfirmationModal}
                onConfirm={handleDeletePlan}
                onCancel={handleCloseConfirmationModal}
                message="Are you sure you want to delete this plan?"
            />
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg font-semibold text-gray-900">Construction Linked Plans</h2>

                {hasPrivilege("canEdit") && (
                    <IconButton
                        onClick={() => {
                            setEditingPlan(null);
                            setOpenNewPlanModal(true);
                        }}
                        icon={<FaPlus className="mr-2" />}
                        left
                    >
                        Add New Plan
                    </IconButton>
                )}
            </div>

            {loading ? (
              <div className="overflow-x-auto">
                <table className="min-w-full min-h-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[1, 2, 3, 4, 5].map((index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-6 bg-gray-200 rounded animate-pulse w-20"></div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-8 bg-gray-200 rounded animate-pulse w-32"></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : plans.length === 0 ? (
              <div className="flex justify-center items-center h-96">
                <div className="text-center max-w-md">
                  {/* Icon */}
                  <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  
                  {/* Title and Description */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">No Construction Plans Yet</h3>
                  <p className="text-gray-500 text-base mb-8 leading-relaxed">
                    Get started by creating your first construction plan to track building progress and manage project phases.
                  </p>
  
                  
                  {/* Additional Info */}
                  <div className="mt-6 text-sm text-gray-400">
                    Construction plans help you organize and track building progress across different phases
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 mt-2">
                {plans.map((plan) => (
                  <div key={plan.constructionLinkedPlanId} className="rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <CustomCollapseCard
                      title={<span className="text-base font-medium text-gray-900">{plan.name}</span>}
                      isOpen={expandedPlanId === plan.constructionLinkedPlanId}
                      toggle={() => toggleCard(plan.constructionLinkedPlanId)}
                      contentWithEdit={<div className="p-2">{renderPlanContent(plan)}</div>}
                      showSaveAction={false}
                      uploading={false}
                      additionalButtons={renderAdditionalButtons(plan)}
                    />
                  </div>
                ))}
              </div>
            )}
            
            {error && <p className="text-red-500 text-center py-4">{error}</p>}

            {/* Create / edit construction plan modal */}
            <FormModal
                open={openNewPlanModal}
                onClose={handleCancelEdit}
                title={editingPlan ? "Edit Plan" : "Create New Plan"}
                size="sm"
            >
                <Form
                    key={editingPlan?.constructionLinkedPlanId ?? "new"}
                    schema={constructionPlanSchema}
                    defaultValues={{ name: editingPlan?.name || "" }}
                    onSubmit={editingPlan ? handleUpdatePlan : handleCreatePlan}
                >
                    <Field
                        name="name"
                        label="Plan Name"
                        placeholder="Enter plan name"
                        required
                    />
                    <FormSubmit loadingLabel="Saving...">
                        {editingPlan ? "Update Plan" : "Create Plan"}
                    </FormSubmit>
                </Form>
            </FormModal>

        </div>
    );
};

export default ConstructionPlan;